import { useState, useEffect } from 'react';

const FoodFormModal = ({ isOpen, onClose, onSubmit, title, initialData = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    restaurant: '',
    price: '',
    image: '',
    category: 'healthy',
  });
  const [imageFile, setImageFile] = useState(null);
  const [useFileUpload, setUseFileUpload] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        restaurant: initialData.restaurant,
        price: initialData.price.toString(),
        image: initialData.image,
        category: initialData.category,
      });
      setImageFile(null);
      setUseFileUpload(false);
    } else {
      setFormData({
        name: '',
        restaurant: '',
        price: '',
        image: '',
        category: 'healthy',
      });
      setImageFile(null);
      setUseFileUpload(false);
    }
    setErrors({});
  }, [initialData, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Food name is required';
    if (!formData.restaurant.trim()) newErrors.restaurant = 'Restaurant name is required';
    if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0)
      newErrors.price = 'Valid price is required';

    if (useFileUpload && !imageFile) {
      newErrors.image = 'Please upload an image file';
    } else if (!useFileUpload && !formData.image.trim()) {
      newErrors.image = 'Please enter an image URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const dataToSubmit = new FormData();
      dataToSubmit.append('name', formData.name);
      dataToSubmit.append('restaurant', formData.restaurant);
      dataToSubmit.append('price', formData.price);
      dataToSubmit.append('category', formData.category);

      if (useFileUpload && imageFile) {
        dataToSubmit.append('image', imageFile);
      } else {
        dataToSubmit.append('image', formData.image);
      }

      await onSubmit(dataToSubmit);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) setImageFile(file);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">{title}</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <i className="ri-close-line text-gray-600"></i>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Food Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter food name"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            {/* Restaurant */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Restaurant *
              </label>
              <input
                type="text"
                name="restaurant"
                value={formData.restaurant}
                onChange={handleChange}
                placeholder="Enter restaurant name"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 ${
                  errors.restaurant ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors.restaurant && (
                <p className="text-red-500 text-sm mt-1">{errors.restaurant}</p>
              )}
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price ($) *
              </label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="0.00"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 ${
                  errors.price ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="healthy">Healthy</option>
                <option value="premium">Premium</option>
                <option value="luxury">Luxury</option>
              </select>
            </div>

            {/* Image Input Mode Switch */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Image Source</label>
              <button
                type="button"
                onClick={() => {
                  setUseFileUpload(!useFileUpload);
                  setErrors({});
                }}
                className="text-orange-500 text-sm underline"
              >
                {useFileUpload ? 'Use URL instead' : 'Upload File instead'}
              </button>
            </div>

            {/* Image Upload / URL */}
            {useFileUpload ? (
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg"
                />
                {errors.image && (
                  <p className="text-red-500 text-sm mt-1">{errors.image}</p>
                )}
                {imageFile && (
                  <div className="mt-3">
                    <img
                      src={URL.createObjectURL(imageFile)}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div>
                <input
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 ${
                    errors.image ? 'border-red-500' : 'border-gray-200'
                  }`}
                />
                {errors.image && (
                  <p className="text-red-500 text-sm mt-1">{errors.image}</p>
                )}
                {formData.image && (
                  <div className="mt-3">
                    <img
                      src={formData.image}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-lg"
                      onError={(e) => (e.target.style.display = 'none')}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 border border-gray-200 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : initialData ? 'Update' : 'Add Food'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FoodFormModal;
